import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { 
  ArrowLeft, UserPlus, Users, Shield, Mail, Phone, 
  ToggleLeft, ToggleRight, Copy, Search, Pencil, Trash2, KeyRound
} from "lucide-react";

const roleColors = {
  admin: "bg-purple-100 text-purple-700",
  consultant: "bg-blue-100 text-blue-700",
  field_agent: "bg-amber-100 text-amber-700",
  client: "bg-slate-100 text-slate-700",
};

const roleLabels = {
  admin: "Admin",
  consultant: "Consultant",
  field_agent: "Field Agent",
  client: "Client",
};

const clientTypeColors = {
  sla: "bg-indigo-100 text-indigo-700",
  ad_hoc: "bg-slate-100 text-slate-600",
  product_client: "bg-teal-100 text-teal-700",
};

export default function UserManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "",
    full_name: "",
    phone: "",
    role: "consultant",
    client_type: "ad_hoc",
  });
  const [tempPassword, setTempPassword] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: "", role: "", phone: "", client_type: "", additional_emails: [], linked_client_id: "" });

  const fetchUsers = useCallback(async () => {
    try {
      const res = await apiClient.get("/users");
      setUsers(res.data);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const res = await apiClient.post("/users/invite", inviteForm);
      setTempPassword(res.data.temporary_password);
      toast.success("User invited successfully");
      setInviteForm({ email: "", full_name: "", phone: "", role: "consultant", client_type: "ad_hoc" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to invite user");
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      const res = await apiClient.patch(`/users/${userId}/toggle-status`);
      toast.success(res.data.message);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const openEditDialog = (u) => {
    setEditUser(u);
    setEditForm({
      full_name: u.full_name,
      role: u.role,
      phone: u.phone || "",
      client_type: u.client_type || "ad_hoc",
      additional_emails: u.additional_emails || [],
      linked_client_id: u.linked_client_id || "",
    });
    setNewEmailInput("");
    setEditDialogOpen(true);
  };

  const [newEmailInput, setNewEmailInput] = useState("");
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const addAdditionalEmail = () => {
    const val = newEmailInput.trim();
    if (!val) return;
    if (!emailRegex.test(val)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if ((editForm.additional_emails || []).some((e) => e.toLowerCase() === val.toLowerCase()) || val.toLowerCase() === (editUser?.email || "").toLowerCase()) {
      toast.info("Email already in the list");
      return;
    }
    setEditForm({ ...editForm, additional_emails: [...(editForm.additional_emails || []), val] });
    setNewEmailInput("");
  };
  const removeAdditionalEmail = (e) => {
    setEditForm({ ...editForm, additional_emails: (editForm.additional_emails || []).filter((x) => x !== e) });
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      await apiClient.patch(`/users/${editUser.id}`, editForm);
      toast.success("User updated successfully");
      setEditDialogOpen(false);
      setEditUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm(`Are you sure you want to delete ${u.full_name} (${u.email})? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/users/${u.id}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleResetPassword = async (u) => {
    if (!window.confirm(`Send a password reset email to ${u.email}?`)) return;
    try {
      const res = await apiClient.post(`/admin/reset-user-password/${u.id}`);
      toast.success(`Reset link sent to ${res.data.email_sent_to}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset email");
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(tempPassword);
    toast.success("Password copied to clipboard");
  };

  const filteredUsers = users.filter((u) => {
    const matchesSearch = 
      u.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === "all" || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="user-management-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold text-slate-900">User Management</h1>
            <p className="text-slate-500 mt-1">Manage consultants, field agents, and clients</p>
          </div>
          <Dialog open={inviteDialogOpen} onOpenChange={(open) => {
            setInviteDialogOpen(open);
            if (!open) setTempPassword("");
          }}>
            <DialogTrigger asChild>
              <Button className="bg-sag-navy hover:bg-sag-navy-800" data-testid="invite-user-button">
                <UserPlus className="h-4 w-4 mr-2" />
                Invite User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              {tempPassword ? (
                <>
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">User Invited!</DialogTitle>
                    <DialogDescription>
                      Share the temporary password with the new user.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <Label className="text-emerald-700">Temporary Password</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <code className="flex-1 p-3 bg-white rounded border font-mono text-lg">
                        {tempPassword}
                      </code>
                      <Button variant="outline" size="icon" onClick={copyPassword}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-sm text-emerald-600 mt-2">
                      The user should change this password on first login.
                    </p>
                  </div>
                  <Button 
                    onClick={() => { setInviteDialogOpen(false); setTempPassword(""); }}
                    className="w-full mt-4"
                  >
                    Done
                  </Button>
                </>
              ) : (
                <>
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">Invite New User</DialogTitle>
                    <DialogDescription>
                      Add a consultant or field agent to your team.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleInvite} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="full_name">Full Name</Label>
                      <Input
                        id="full_name"
                        placeholder="John Doe"
                        value={inviteForm.full_name}
                        onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                        required
                        data-testid="invite-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="user@example.com"
                        value={inviteForm.email}
                        onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                        required
                        data-testid="invite-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone (Optional)</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+1 (555) 123-4567"
                        value={inviteForm.phone}
                        onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                        data-testid="invite-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Role</Label>
                      <Select
                        value={inviteForm.role}
                        onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
                      >
                        <SelectTrigger data-testid="invite-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="consultant">Consultant</SelectItem>
                          <SelectItem value="field_agent">Field Agent</SelectItem>
                          <SelectItem value="client">Client</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {inviteForm.role === "client" && (
                      <div className="space-y-2">
                        <Label>Client Type</Label>
                        <Select
                          value={inviteForm.client_type}
                          onValueChange={(value) => setInviteForm({ ...inviteForm, client_type: value })}
                        >
                          <SelectTrigger data-testid="invite-client-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sla">SLA Client (Contracted Hours)</SelectItem>
                            <SelectItem value="product_client">Product Client (Support Included)</SelectItem>
                            <SelectItem value="ad_hoc">Ad Hoc Client (Pay-Per-Service)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-sag-navy hover:bg-sag-navy-800" data-testid="send-invite-button">
                        Send Invite
                      </Button>
                    </div>
                  </form>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <Card className="sag-card">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-users-input"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-[180px]" data-testid="role-filter-select">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                  <SelectItem value="consultant">Consultants</SelectItem>
                  <SelectItem value="field_agent">Field Agents</SelectItem>
                  <SelectItem value="client">Clients</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="sag-card">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading users...</div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No users found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell>
                        <div className="font-medium text-slate-900">{u.full_name}</div>
                        {u.phone && (
                          <div className="text-sm text-slate-500 flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {u.phone}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-slate-600">
                          <Mail className="h-3 w-3" />
                          {u.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={roleColors[u.role]} variant="secondary">
                          {roleLabels[u.role]}
                        </Badge>
                        {u.role === "client" && u.client_type && (
                          <Badge className={`ml-1 ${clientTypeColors[u.client_type] || ""}`} variant="secondary" data-testid={`client-type-badge-${u.id}`}>
                            {u.client_type === "sla" ? "SLA" : u.client_type === "product_client" ? "Product" : "Ad Hoc"}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {u.is_active ? (
                          <Badge className="bg-emerald-100 text-emerald-700" variant="secondary">
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700" variant="secondary">
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {new Date(u.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {u.id !== user?.id && (
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(u)} data-testid={`edit-user-${u.id}`}>
                              <Pencil className="h-4 w-4 text-slate-500" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleResetPassword(u)} title="Send password reset email" data-testid={`reset-pw-${u.id}`}>
                              <KeyRound className="h-4 w-4 text-amber-500" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleToggleStatus(u.id)} data-testid={`toggle-user-${u.id}`}>
                              {u.is_active ? <ToggleRight className="h-5 w-5 text-emerald-600" /> : <ToggleLeft className="h-5 w-5 text-slate-400" />}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u)} data-testid={`delete-user-${u.id}`}>
                              <Trash2 className="h-4 w-4 text-red-400" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-[450px]">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Edit User</DialogTitle>
              <DialogDescription>{editUser?.email}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditUser} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} required data-testid="edit-user-name" />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="consultant">Consultant</SelectItem>
                    <SelectItem value="field_agent">Field Agent</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="Optional" />
              </div>
              {editForm.role === "client" && (
                <div className="space-y-2">
                  <Label>Client Type</Label>
                  <Select value={editForm.client_type} onValueChange={(v) => setEditForm({ ...editForm, client_type: v })}>
                    <SelectTrigger data-testid="edit-user-client-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sla">SLA Client (Contracted Hours)</SelectItem>
                      <SelectItem value="product_client">Product Client (Support Included)</SelectItem>
                      <SelectItem value="ad_hoc">Ad Hoc Client (Pay-Per-Service)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {editForm.role === "client" && (
                <div className="space-y-2">
                  <Label>Link to Parent Client (optional)</Label>
                  <p className="text-xs text-slate-500 -mt-1">If this user is a CFO/AP contact for another client, link them here so they see the parent&apos;s invoices under their own login.</p>
                  <Select
                    value={editForm.linked_client_id || "none"}
                    onValueChange={(v) => setEditForm({ ...editForm, linked_client_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="edit-user-linked-client"><SelectValue placeholder="No parent — standalone client" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— No parent (standalone client) —</SelectItem>
                      {users
                        .filter((u) => u.role === "client" && u.id !== editUser?.id && !u.linked_client_id)
                        .map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.email})</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Additional Notification Emails</Label>
                <p className="text-xs text-slate-500 -mt-1">CC&apos;d on invoices, reminders, and receipts. Ideal for CFO, accountant, or ops. Press Enter to add.</p>
                <div className="flex flex-wrap gap-2 min-h-[36px] p-2 border rounded-md bg-slate-50">
                  {(editForm.additional_emails || []).length === 0 && (
                    <span className="text-xs text-slate-400 self-center">No additional emails yet.</span>
                  )}
                  {(editForm.additional_emails || []).map((e) => (
                    <span key={e} className="inline-flex items-center gap-1 bg-white border border-slate-300 rounded-full pl-3 pr-1 py-1 text-xs" data-testid={`additional-email-tag-${e}`}>
                      {e}
                      <button
                        type="button"
                        onClick={() => removeAdditionalEmail(e)}
                        className="ml-1 h-4 w-4 rounded-full bg-slate-200 hover:bg-red-100 text-slate-600 hover:text-red-700 text-[10px] flex items-center justify-center"
                        aria-label={`Remove ${e}`}
                      >×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={newEmailInput}
                    onChange={(e) => setNewEmailInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAdditionalEmail(); } }}
                    placeholder="cfo@company.com"
                    data-testid="additional-email-input"
                  />
                  <Button type="button" variant="outline" onClick={addAdditionalEmail} data-testid="add-additional-email-btn">Add</Button>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-sag-navy hover:bg-sag-navy-800" data-testid="save-edit-user">Save Changes</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
